"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { BASE_PATH } from "@/constants";
import { AppConfig } from "@/types/appConfig";

type AppConfigContextType = {
  appConfig: AppConfig | null;
  loading: boolean;
};

const AppConfigContext = createContext<AppConfigContextType | undefined>(
  undefined
);

export function AppConfigProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [appConfig, setAppConfig] =
    useState<AppConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchConfig() {
      try {
        const response = await fetch(`${BASE_PATH}/api/config`);

        if (!response.ok) {
          throw new Error(
            `Failed to fetch app config: ${response.statusText}`
          );
        }

        const data: AppConfig = await response.json();
        setAppConfig(data);
      } catch (err) {
        console.error("App config error:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchConfig();
  }, []);

  return (
    <AppConfigContext.Provider value={{ appConfig, loading }}>
      {children}
    </AppConfigContext.Provider>
  );
}

export function useAppConfig() {
  const context = useContext(AppConfigContext);

  if (!context) {
    throw new Error(
      "useAppConfig must be used within AppConfigProvider"
    );
  }

  return context;
}